import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { post } from 'aws-amplify/api';
import awsconfig from '../aws-exports';
import { setDocumentTitle } from '../utils/titleManager';
import { validateRequiredFields } from '../utils/eventValidation';
import { validateDate, validateGuestLimit, validateDescription, validateAdditionalInfo } from '../utils/validation';
import { sanitizeFilename, sanitizeText } from '../utils/inputSanitization';
import { validateCssFile, uploadCssFile } from '../utils/cssUploadUtils';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { showAlert } from '../utils/alerts';
import { validateRsvpCutoff } from '../utils/rsvpUtils';

Amplify.configure(awsconfig);

// API configuration is handled by Amplify.configure(awsconfig)

function CreateEventScreen() {
  useEffect(() => {
    setDocumentTitle('Create Event');
  }, []);

  const [customCssType, setCustomCssType] = useState('url');
  const [customCssUrl, setCustomCssUrl] = useState('');
  const [customCss, setCustomCss] = useState(null);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [heading, setHeading] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [rsvpDetails, setRsvpDetails] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [recapText, setRecapText] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [rsvpCutoff, setRsvpCutoff] = useState('');
  const [customImages, setCustomImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const handleCssUpload = async () => {
    if (!customCss) return null;
    try {
      validateCssFile(customCss);
      const response = await uploadCssFile(customCss);
      return response.url;
    } catch (error) {
      throw new Error(`CSS Upload failed: ${error.message}`);
    }
  };

  const handleFileUpload = async (file, type) => {
    const sanitizedName = sanitizeFilename(file.name);
    const fileName = `${Date.now()}-${sanitizedName}`;
    
    if (type === 'css' && file.type !== 'text/css') {
      throw new Error('Invalid CSS file type');
    }
    if (type === 'image' && !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      throw new Error('Invalid image file type');
    }
    
    const response = await fetch(`/api/upload-url?fileName=${encodeURIComponent(fileName)}&type=${encodeURIComponent(file.type)}`);
    const { uploadUrl, fileUrl } = await response.json();
    
    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    return fileUrl;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationFields = {
        name,
        date,
        heading,
        location,
        instructions,
        rsvpDetails
      };

      //validateRequiredFields(validationFields);
      validateDate(date);
      validateGuestLimit(maxGuests);
      validateDescription(description);
      validateAdditionalInfo(additionalInfo);
      
      // Validate RSVP cutoff if provided
      if (rsvpCutoff) {
        const cutoffValidation = validateRsvpCutoff(rsvpCutoff, date);
        if (!cutoffValidation.isValid) {
          throw new Error(cutoffValidation.error);
        }
      }
      
      let customCssUrl = null;
      let customImageUrls = [];

      if (customCss) {
        if (!customCss.type || customCss.type !== 'text/css') {
          throw new Error('Please upload a valid CSS file');
        }

        if (!(/^[\w-]+[.]css$/i).test(customCss.name) || customCss.name.length > 255) {
          throw new Error('Invalid filename. Use only letters, numbers, hyphens and underscores');
        }

        customCssUrl = await handleFileUpload(customCss, 'css');
      }

      if (customImages.length > 0) {
        const imagePromises = customImages.map(async (file) => {
          if (!file.type || !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
            throw new Error('Please upload only JPEG, PNG, GIF or WebP images');
          }

          if (!/^[\w-]+$/.test(file.name) || file.name.length > 255) {
            throw new Error('Invalid filename. Use only letters, numbers, hyphens and underscores');
          }
          return handleFileUpload(file, 'image');
        });

        customImageUrls = await Promise.all(imagePromises);
      }

      const eventData = {
        name: sanitizeText(name),
        date,
        heading: sanitizeText(heading),
        location: sanitizeText(location),
        description: sanitizeText(description),
        instructions: sanitizeText(instructions),
        rsvpDetails: sanitizeText(rsvpDetails),
        additionalInfo: additionalInfo ? sanitizeText(additionalInfo) : '',
        recapText: recapText ? sanitizeText(recapText) : '',
        maxGuests: maxGuests ? parseInt(maxGuests) : null,
        rsvpCutoff: rsvpCutoff || null,
        customCssUrl: customCssType === 'url' ? customCssUrl : await handleCssUpload(),
        customImageUrls
      };

      // Get current authenticated session

      const data = await post({
        apiName: 'events',
        path: '/events',
        withCredentials: true,
        options: {
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        }
      });
      const { id } = data;

      if (!id) {
        throw new Error('Failed to create event: Invalid response');
      }

      showAlert('Event created successfully', 'success');
      window.location.href = `/events/${id}`;
    } catch (err) {
      console.error('Error creating event:', err);
      let errorMessage = 'Failed to create event. Please try again.';
      
      if (err.name === 'UnknownError') {
        // Check if it's a network connectivity issue
        if (!navigator.onLine) {
          errorMessage = 'No internet connection. Please check your network and try again.';
        } else {
          errorMessage = 'Unable to reach the server. Please try again later.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showAlert(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return <LoadingSpinner message="Creating new event" />;
  }

  return (
    <div className="create-event">
      <h1 className="create-event__title">Create New Event</h1>
      
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <form className="form" onSubmit={onSubmit}>
        <div className="form__group">
          <label htmlFor="name" className="form__label">
            Event Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(sanitizeText(e.target.value))}
            placeholder="Enter event name"
            className="form__input"
          />
        </div>

        <div className="form__group">
          <label htmlFor="date" className="form__label">
            Date
          </label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="form__input"
          />
        </div>

        <div className="form__group">
          <label className="form__label">Custom CSS</label>
          <div className="mb-2">
            <select 
              value={customCssType}
              onChange={(e) => setCustomCssType(e.target.value)}
              className="form__input"
            >
              <option value="url">Enter URL</option>
              <option value="file">Upload File</option>
            </select>
          </div>
          
          {customCssType === 'url' ? (
            <input
              type="url"
              value={customCssUrl}
              onChange={(e) => setCustomCssUrl(e.target.value)}
              placeholder="Enter CSS URL"
              className="form__input"
            />
          ) : (
            <input
              type="file"
              accept=".css"
              onChange={(e) => setCustomCss(e.target.files[0])}
              className="form__input"
            />
          )}
        </div>

        <div className="form__group">
          <label htmlFor="heading" className="form__label">
            Heading
          </label>
          <input
            id="heading"
            type="text"
            value={heading}
            onChange={(e) => setHeading(sanitizeText(e.target.value))}
            placeholder="Enter event heading"
            className="form__input"
          />
        </div>

        <div className="form__group">
          <label htmlFor="location" className="form__label">
            Location
          </label>
          <input
            id="location"
            type="text"
            value={location}
            onChange={(e) => setLocation(sanitizeText(e.target.value))}
            placeholder="Enter event location"
            className="form__input"
          />
        </div>

        <div className="form__group">
          <label htmlFor="instructions" className="form__label">
            Instructions
          </label>
          <textarea
            id="instructions"
            value={instructions}
            onChange={(e) => setInstructions(sanitizeText(e.target.value))}
            placeholder="Enter event instructions"
            rows={3}
            className="form__input form__input--multiline"
          />
        </div>

        <div className="form__group">
          <label htmlFor="rsvpDetails" className="form__label">
            RSVP Details
          </label>
          <textarea
            id="rsvpDetails"
            value={rsvpDetails}
            onChange={(e) => setRsvpDetails(sanitizeText(e.target.value))}
            placeholder="Enter RSVP details"
            rows={3}
            className="form__input form__input--multiline"
          />
        </div>

        <div className="form__group">
          <label htmlFor="maxGuests" className="form__label">
            Maximum Guests (Optional)
          </label>
          <input
            id="maxGuests"
            type="number"
            value={maxGuests}
            onChange={(e) => setMaxGuests(e.target.value)}
            placeholder="Enter maximum number of guests"
            min="1"
            className="form__input"
          />
        </div>

        <div className="form__group">
          <label htmlFor="rsvpCutoff" className="form__label">
            RSVP Cutoff Date (Optional)
          </label>
          <input
            id="rsvpCutoff"
            type="datetime-local"
            value={rsvpCutoff}
            onChange={(e) => setRsvpCutoff(e.target.value)}
            className="form__input"
          />
        </div>

        <div className="form__group">
          <label htmlFor="additionalInfo" className="form__label">
            Additional Information (Optional)
          </label>
          <textarea
            id="additionalInfo"
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(sanitizeText(e.target.value))}
            placeholder="Enter additional information"
            rows={3}
            className="form__input form__input--multiline"
          />
        </div>

        <div className="form__group">
          <label htmlFor="recapText" className="form__label">
            Recap Text (Optional)
          </label>
          <textarea
            id="recapText"
            value={recapText}
            onChange={(e) => setRecapText(sanitizeText(e.target.value))}
            placeholder="Enter text for the recap page (supports Markdown)"
            rows={4}
            className="form__input form__input--multiline"
          />
          <small className="form__help">
            This text will be displayed on the recap page. Supports Markdown formatting (e.g., **bold**, *italic*, [links](url)).
          </small>
        </div>

        <div className="form__group">
          <label htmlFor="description" className="form__label">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(sanitizeText(e.target.value))}
            placeholder="Enter event description"
            rows={4}
            className="form__input form__input--multiline"
          />
        </div>

        <div className="form__group">
          <label htmlFor="customCss" className="form__label">
            Custom CSS (Optional)
          </label>
          <input
            id="customCss"
            type="file"
            accept=".css"
            onChange={(e) => setCustomCss(e.target.files[0])}
            className="form__input"
          />
          <small className="form__help">
            Upload a CSS file to customize your invitation&apos;s appearance
          </small>
        </div>

        <div className="form__group">
          <label className="form__label">Event Images</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            onChange={(e) => setCustomImages(Array.from(e.target.files))}
            className="form__input"
          />
        </div>

        <button 
          type="submit" 
          className="form__submit" 
          disabled={isSubmitting}
        >
          Create Event
        </button>
      </form>
    </div>
  );
}

export default CreateEventScreen;